import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, ListItemText, ListItemIcon, Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

type Option = {
  display: string;
  value: string;
};

type Props = {
  onSelect: (value: string) => void;
  shippingOptions: Option[];
  placeholder?: string;
  required?: boolean;
};

const ShippingOptionSelect: React.FC<Props> = ({
  onSelect,
  shippingOptions,
  placeholder = 'Select carrier preference',
  required = false,
}) => {
  const [mainOptions, setMainOptions] = useState<Option[]>([]);
  const [otherOptions, setOtherOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<null | HTMLElement>(null);

  const [menuWidth, setMenuWidth] = useState<number>(0);
  const inputRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);
  const subOpen = Boolean(subAnchorEl);

  useEffect(() => {
    const hardcoded = ['fastest', 'cheapest'];
    const main = shippingOptions.filter(opt => hardcoded.includes(opt.value.toLowerCase()));
    const other = shippingOptions.filter(opt => !hardcoded.includes(opt.value.toLowerCase()));
    setMainOptions(main);
    setOtherOptions(other);
    setSelectedOption(null); // reset selected when options change
  }, [shippingOptions]);

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    if (inputRef.current) {
      setMenuWidth(inputRef.current.offsetWidth);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSubAnchorEl(null);
  };

  const handleSelect = (option: Option) => {
    setSelectedOption(option);
    onSelect(option.value);
    handleClose();
  };

  const handleOtherHover = (event: React.MouseEvent<HTMLElement>) => {
    setSubAnchorEl(event.currentTarget);
  };

  return (
    <>
      {/* Custom Input */}
      <Box
        ref={inputRef}
        onClick={handleOpen}
        sx={{
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px 12px',
          minWidth: '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          color: selectedOption ? 'inherit' : '#999',
          fontSize: '16px',
        }}
      >
        <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '16px' }}>
          {selectedOption?.display || `${placeholder}${required ? ' *' : ''}`}
        </Typography>
        {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      </Box>

      {/* Main Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: menuWidth,
          },
        }}
      >
        {mainOptions.map(opt => (
          <MenuItem key={opt.value} onClick={() => handleSelect(opt)}>
            {opt.display}
          </MenuItem>
        ))}
        {otherOptions.length > 0 && (
          <MenuItem onMouseEnter={handleOtherHover}>
            <ListItemText primary="Other" />
            <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
              <ChevronRightIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}
      </Menu>

      {/* Submenu for Other */}
      <Menu
        anchorEl={subAnchorEl}
        open={subOpen}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        {otherOptions.map(opt => (
          <MenuItem key={opt.value} onClick={() => handleSelect(opt)}>
            {opt.display}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ShippingOptionSelect;
